import { useGetEnrollmentMultiDisclosuresData, useSubmitEnrollmentMultiDisclosuresData } from '@egw/api'
import { Button, Alert, AlertType, GenericError, ProgressBar } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useMemo, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { DisclosureSection, LoadingAnimation, PageHeader } from '../../../components'
import { type Config } from '../../../config/config'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { getArticlePath } from '../../../utils/disclosures/get-disclosures-path'
import { getMultiDisclosures } from '../../../utils/disclosures/get-multi-disclosures'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Multi - Disclosures')

export const DisclosuresPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config

  const { data, isLoading, error } = useGetEnrollmentMultiDisclosuresData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiDisclosuresData()

  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.USER_DISCLOSURES_STARTED)
  }, [])

  const disclosures = useMemo(() => {
    return getMultiDisclosures(config.zendeskArticles, data?.enrollmentData?.userInfo?.investment?.disclosures ?? [])
  }, [config.zendeskArticles, data?.enrollmentData?.userInfo?.investment?.disclosures])

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: {
      egwa: false,
    },
  })

  const isCheckedEGWA = watch('egwa')

  // Dispatch events when disclosure section is checked
  useEffect(() => {
    if (isCheckedEGWA) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_WEALTHADVISORS_CONSENT)
    }
  }, [isCheckedEGWA])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (): Promise<void> => {
    if (isValid) {
      const apexDisclosuresPayload =
        data?.enrollmentData?.userInfo?.investment?.disclosures?.map((e) => e.disclosureId) ?? []

      const payload = {
        depositAgreementDisclosure: {
          id: getArticlePath(config.zendeskArticles.depositAgreement),
          accepted: true,
        },
        taxCertificationForUSPersonsDisclosure: {
          id: getArticlePath(config.zendeskArticles.taxCertification),
          accepted: true,
        },
        privacyNotice: {
          id: getArticlePath(config.zendeskArticles.privacyPolicySection),
          accepted: true,
        },
        investmentAdvisoryAgreementDisclosure: {
          id: getArticlePath(config.zendeskArticles.investmentAdvisoryAgreement),
          accepted: true,
        },
        advWrapFeeDisclosure: {
          id: getArticlePath(config.zendeskArticles.advPart2A2b),
          accepted: true,
        },
        advisoryPrivacyNotice: {
          id: getArticlePath(config.zendeskArticles.privacyPolicyAdvisors),
          accepted: true,
        },
        customerRelationshipSummaryDisclosure: {
          id: getArticlePath(config.zendeskArticles.customerRelationshipAgreement),
          accepted: true,
        },
        investmentProfile: {
          acceptedDisclosureIds: apexDisclosuresPayload,
        },
      }

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_CREATEACCOUNT_SUBMIT)
      sendEvent(EDataLayerEventWealth.MILESTONE_DISCLOSURES_CONSENT, {
        disclosures: payload,
      })
      sendEvent(EDataLayerEventWealth.MILESTONE_CREATE_ACCOUNT_CLICKED)

      try {
        const response = await mutate(payload)
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.EGM_SUBMISSION_DISCLOSURES_ACCEPTED)
          sendEvent(EDataLayerEventWealth.MILESTONE_ACCOUNT_CREATED)
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_ACCOUNT_CREATED)
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  const description1Key = 'multi.disclosures.description_1_yeet_coastal'
  const description2Key = 'multi.disclosures.description_2_joint_enrollment'

  return (
    <EnrollmentMultiLayout title={t('multi.disclosures.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        isSubmitting ? (
          <div className="flex flex-col justify-center items-center flex-1 w-full">
            <LoadingAnimation />
            <p className="typo-primary-xl-light mt-md">{t(`multi.disclosures.loading`)}</p>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <PageHeader title={t('multi.disclosures.title')} />
            <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
            <p className="typo-primary-base text-text-meta mb-md">{t(description1Key)}</p>

            <p className="typo-primary-base text-text-meta mb-md">{t(description2Key)}</p>

            {disclosures.map((e, i) => {
              return (
                <Controller
                  name={e.name as 'egwa'}
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <DisclosureSection
                      checkboxHelperText={e.helper}
                      checkboxLabel={e.title}
                      expandedLinks={e.links}
                      expandedText={e.expanded}
                      hasBorder={disclosures.length !== i + 1}
                      testId={`disclosure-${e.title.toLowerCase().replaceAll(' ', '-')}`}
                      onCheckboxChange={field.onChange}
                    />
                  )}
                  key={`disclosure-${e.title.replaceAll(' ', '-')}`}
                />
              )
            })}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('multi.disclosures.continue')}
            </Button>
          </form>
        )
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
